import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown, DollarSign, ShoppingBag, Boxes, Users } from "lucide-react";

const money = (c) => `$${(((c || 0) / 100)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Trend({ pct }) {
  if (pct == null) return null;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${up ? "text-emerald-600" : "text-[#E63946]"}`} data-testid="trend">
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{Math.abs(pct)}%
    </span>
  );
}

function Card({ icon: Icon, label, value, trend, testid, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="label-overline text-[10px]">{label}</span>
        <Icon className="w-4 h-4 text-slate-300" />
      </div>
      <div className="mt-1.5 flex items-baseline gap-2">
        <span className="font-data text-2xl text-[#0A192F]">{value}</span>
        {trend != null && <Trend pct={trend} />}
      </div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function Table({ title, rows, cols, testid, empty }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid={testid}>
      <div className="label-overline text-[10px] mb-2">{title}</div>
      {rows.length === 0 ? <div className="text-sm text-slate-400 py-3">{empty || "No data yet."}</div> : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] uppercase text-slate-400">{cols.map((c) => <th key={c.key} className="py-1 font-semibold">{c.label}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-slate-50">{cols.map((c) => <td key={c.key} className="py-1.5">{c.render ? c.render(r) : r[c.key]}</td>)}</tr>)}</tbody>
        </table>
      )}
    </div>
  );
}

export default function MarketplaceAnalytics() {
  const [d, setD] = useState(null);
  const [prodSort, setProdSort] = useState("revenue_cents");
  useEffect(() => { api.get("/admin/marketplace/analytics").then(({ data }) => setD(data)).catch(() => setD(false)); }, []);
  if (d === null) return <div className="py-16 text-center text-slate-400" data-testid="analytics-loading">Loading analytics…</div>;
  if (d === false) return <div className="py-16 text-center text-[#E63946]">Could not load analytics.</div>;

  const s = d.sales, o = d.orders, p = d.products, c = d.customers, col = d.collections;
  const sortedProducts = [...p.best_selling].sort((a, b) => (b[prodSort === "inventory" ? "units" : prodSort] || 0) - (a[prodSort === "inventory" ? "units" : prodSort] || 0));
  const invRows = prodSort === "inventory" ? p.lowest_inventory : sortedProducts;

  return (
    <div className="space-y-5" data-testid="marketplace-analytics">
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card icon={DollarSign} label="Revenue Today" value={money(s.today.cents)} trend={s.today.change_pct} testid="card-rev-today" />
        <Card icon={ShoppingBag} label="Orders Today" value={o.today} testid="card-orders-today" sub={`${o.total} lifetime`} />
        <Card icon={DollarSign} label="Avg Order Value" value={money(o.aov_cents)} testid="card-aov" />
        <Card icon={Boxes} label="Inventory Value" value={money(p.inventory_value_cents)} testid="card-inv-value" sub={`${p.out_of_stock.length} out of stock`} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card icon={DollarSign} label="Revenue This Week" value={money(s.week.cents)} trend={s.week.change_pct} testid="card-rev-week" />
        <Card icon={DollarSign} label="Revenue This Month" value={money(s.month.cents)} trend={s.month.change_pct} testid="card-rev-month" />
        <Card icon={DollarSign} label="Revenue This Year" value={money(s.year.cents)} testid="card-rev-year" />
        <Card icon={DollarSign} label="Lifetime Revenue" value={money(s.lifetime.cents)} testid="card-rev-life" />
      </div>

      {/* Future placeholders */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card icon={ShoppingBag} label="Conversion Rate" value="—" testid="card-conversion" sub="Coming soon" />
        <Card icon={ShoppingBag} label="Cart Abandonment" value="—" testid="card-abandon" sub="Coming soon" />
        <Card icon={Users} label="Total Customers" value={c.total} testid="card-customers" sub={`${c.returning} returning`} />
        <Card icon={Users} label="New This Month" value={c.new_this_month} testid="card-new-customers" />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-3">
        <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="chart-revenue">
          <div className="label-overline text-[10px] mb-2">Revenue by Month</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={d.series}><CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" /><XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${Math.round(v / 100)}`} /><Tooltip formatter={(v) => money(v)} /><Bar dataKey="revenue_cents" fill="#E63946" radius={[3, 3, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="chart-orders">
          <div className="label-overline text-[10px] mb-2">Orders by Month</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={d.series}><CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" /><XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} allowDecimals={false} /><Tooltip /><Line type="monotone" dataKey="orders" stroke="#0A192F" strokeWidth={2} dot={false} /></LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Products */}
      <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="analytics-products">
        <div className="flex items-center justify-between mb-2">
          <span className="label-overline text-[10px]">Products</span>
          <select data-testid="product-sort" value={prodSort} onChange={(e) => setProdSort(e.target.value)} className="border border-slate-300 rounded-sm px-2 py-1 text-xs bg-white">
            <option value="revenue_cents">Sort: Revenue</option>
            <option value="units">Sort: Units Sold</option>
            <option value="inventory">Sort: Inventory Remaining</option>
          </select>
        </div>
        <Table title="" testid="tbl-products" rows={invRows} empty="No sales yet."
          cols={prodSort === "inventory"
            ? [{ key: "name", label: "Product" }, { key: "inventory", label: "Inventory", render: (r) => r.inventory }]
            : [{ key: "name", label: "Product" }, { key: "units", label: "Units" }, { key: "revenue_cents", label: "Revenue", render: (r) => money(r.revenue_cents) }]} />
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        <Table title="Best Selling Variants" testid="tbl-variants" rows={p.best_variants} empty="No sales yet."
          cols={[{ key: "label", label: "Variant" }, { key: "units", label: "Units" }, { key: "revenue_cents", label: "Revenue", render: (r) => money(r.revenue_cents) }]} />
        <Table title="Lowest Inventory" testid="tbl-lowinv" rows={p.lowest_inventory} empty="No tracked stock."
          cols={[{ key: "name", label: "Product" }, { key: "inventory", label: "Left", render: (r) => <span className={r.inventory <= 0 ? "text-[#E63946]" : r.inventory <= 5 ? "text-amber-600" : ""}>{r.inventory}</span> }]} />
        <Table title="Top Customers by Revenue" testid="tbl-customers" rows={c.top_customers} empty="No customers yet."
          cols={[{ key: "name", label: "Customer", render: (r) => r.name || r.email }, { key: "orders", label: "Orders" }, { key: "revenue_cents", label: "Revenue", render: (r) => money(r.revenue_cents) }]} />
        <Table title="Top Selling Collections" testid="tbl-collections" rows={col.top_selling} empty="No sales yet."
          cols={[{ key: "name", label: "Collection" }, { key: "units", label: "Units" }, { key: "revenue_cents", label: "Revenue", render: (r) => money(r.revenue_cents) }]} />
      </div>
      <p className="text-[11px] text-slate-400">Most-viewed products/collections & customer lifetime value coming soon.</p>
    </div>
  );
}
